// frontend/src/pages/Register.jsx
// Standalone signup page. Shows form → "check your email" screen.
// Does NOT auto-login or redirect. User must confirm email first.

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { useLanguage } from '../utils/i18n';
import { validateField } from '../utils/validation';
import LanguageToggle from '../components/LanguageToggle';
import Logo from '../components/Logo';
import { ArrowLeft } from 'lucide-react';

export default function Register() {
    const { session, loading: authLoading, isEmailConfirmed, subscriptionStatus } = useAuth();
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [bizName, setBizName] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [acceptPrivacy, setAcceptPrivacy] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [signupDone, setSignupDone] = useState(false);

    // If already logged in + confirmed, redirect appropriately
    if (!authLoading && session && isEmailConfirmed) {
        if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') {
            return <Navigate to="/dashboard" replace />;
        }
        return <Navigate to="/pricing" replace />;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!validateField('name', bizName, true)) {
            setError(t('err_valid_name'));
            setLoading(false);
            return;
        }
        if (!validateField('email', email, true)) {
            setError(t('err_valid_email'));
            setLoading(false);
            return;
        }
        if (password.length < 6) {
            setError(t('err_password_min'));
            setLoading(false);
            return;
        }
        if (!/\d/.test(password)) {
            setError(t('err_password_weak'));
            setLoading(false);
            return;
        }
        if (!acceptPrivacy) {
            setError(t('err_accept_privacy'));
            setLoading(false);
            return;
        }

        try {
            const { api } = await import('../utils/api');
            const lang = localStorage.getItem('appLang') || 'en';
            const REQUIRE_CONFIRM = process.env.REACT_APP_REQUIRE_EMAIL_CONFIRMATION === 'true' || process.env.REACT_APP_REQUIRE_EMAIL_CONFIRMATION === undefined;

            const res = await api.register({
                email,
                password,
                businessName: bizName || 'My Business',
                lang
            });

            if (res.requireConfirmation || REQUIRE_CONFIRM) {
                setSignupDone(true);
            } else {
                // If no confirmation required, redirect to login
                setError('');
                window.location.href = '/login';
            }
        } catch (err) {
            console.error('[Register Error]', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', position: 'relative' }}>
            {/* Header */}
            <header className="header-container" style={{ position: 'relative', zIndex: 10 }}>
                <Link to="/" className="btn btn-outline glass-btn header-back-minimal" style={{ textDecoration: 'none' }}>
                    <ArrowLeft size={18} /> <span className="hide-mobile" style={{ marginLeft: 8 }}>{t('back_to_home')}</span>
                </Link>
                <div className="header-centered-logo">
                    <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
                        <Logo size={24} />
                        <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)', letterSpacing: -0.5 }}>Wafiyo</span>
                    </Link>
                </div>
                <LanguageToggle />
            </header>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', position: 'relative' }}>
                <div className="bg-shapes hide-mobile">
                    <div className="shape shape-1" style={{ top: '-10%', left: '-10%', width: 600, height: 600, opacity: 0.15 }}></div>
                    <div className="shape shape-2" style={{ bottom: '-10%', right: '-10%', width: 800, height: 800, opacity: 0.1 }}></div>
                </div>

                <div className="glass-card" style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
                    {signupDone ? (
                        /* ─── "Check your email" screen ─── */
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>✉️</div>
                            <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                                {t('check_email_in')} <span style={{ color: 'var(--primary)' }}>{email}</span>
                            </h2>
                            <p style={{ color: 'var(--text-muted)', fontSize: 15, lineHeight: 1.5, marginBottom: 24 }}>
                                {t('check_email_msg')}
                            </p>
                            <p style={{ color: '#8b949e', fontSize: 13 }}>
                                {t('check_spam')}
                            </p>
                        </div>
                    ) : (
                        /* ─── Signup form ─── */
                        <>
                            <div style={{ textAlign: 'center', marginBottom: 28 }}>
                                <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>{t('loyalty_card_manager')}</h1>
                                <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
                                    {t('create_biz_account')}
                                </p>
                            </div>

                            {error && <div className="alert alert-error">{error}</div>}

                            <form onSubmit={handleSubmit} noValidate>
                                <div className="form-group">
                                    <label>{t('business_name')}</label>
                                    <input value={bizName} onChange={e => setBizName(e.target.value)} placeholder="e.g. Mike's Barbershop" required />
                                </div>
                                <div className="form-group">
                                    <label>{t('email')}</label>
                                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
                                </div>
                                <div className="form-group">
                                    <label>{t('password')}</label>
                                    <div className="password-wrapper">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            required
                                            minLength={6}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                            tabIndex="-1"
                                        >
                                            {showPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <label className="privacy-checkbox" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', marginTop: 12, lineHeight: 1.5 }}>
                                    <input
                                        type="checkbox"
                                        checked={acceptPrivacy}
                                        onChange={e => setAcceptPrivacy(e.target.checked)}
                                        style={{ marginTop: 3, accentColor: 'var(--primary)', width: 16, height: 16, flexShrink: 0 }}
                                    />
                                    <span>
                                        {t('accept_privacy_prefix')}{' '}
                                        <Link to="/privacy" target="_blank" style={{ color: '#60a5fa', textDecoration: 'underline' }}>{t('privacy_policy_title')}</Link>
                                        {' '}{t('and')}{' '}
                                        <Link to="/terms" target="_blank" style={{ color: '#60a5fa', textDecoration: 'underline' }}>{t('tos_title')}</Link>
                                    </span>
                                </label>
                                <button className="btn btn-primary btn-glow" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
                                    {loading ? t('please_wait') : t('create_account')}
                                </button>
                            </form>

                            <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: 'var(--text-muted)' }}>
                                {t('already_have_account')}
                                <Link to="/login" style={{ background: 'none', border: 'none', color: '#60a5fa', fontWeight: 600, textDecoration: 'none' }}>
                                    {t('sign_in')}
                                </Link>
                            </p>
                        </>
                    )}
                </div>
            </div>

            {/* Footer */}
            <footer className="landing-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <Logo size={28} />
                            <span className="logo-text">Wafiyo</span>
                            <p className="footer-tagline">{t('footer_tagline')}</p>
                        </div>
                        <div className="footer-links">
                            <div className="link-col">
                                <h4>{t('legal')}</h4>
                                <Link to="/privacy">{t('privacy_policy_title')}</Link>
                                <Link to="/terms">{t('tos_title')}</Link>
                                <Link to="/refund">{t('refund_policy_title')}</Link>
                                <Link to="/contact">{t('contact_title')}</Link>
                            </div>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>© {new Date().getFullYear()} Wafiyo. {t('all_rights_reserved')}</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
